'use client';

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { NeedsAuth } from '@/components/NeedsAuth';
import { PageTitle } from '@/components/PageTitle';
import { Pill, PillGroup } from '@/components/Pill';
import { getClient } from '@/lib/api';
import { money, pct, shortDate } from '@/lib/fmt';
import { fromEur, useFxRates, type FxRates } from '@/lib/fx';
import type { components } from '@/lib/api.types';

type Holding = components['schemas']['HoldingOut'];
type HoldingCreate = components['schemas']['HoldingCreate'];

const KINDS = ['stock', 'crypto'] as const;
type Kind = (typeof KINDS)[number];

// Display-only switch — storage is always EUR. Keep this list small; add
// codes only when there's a real reason (the user lives in € and asked
// for a $ alternative).
const DISPLAY_CURRENCIES = ['EUR', 'USD'] as const;
type DisplayCurrency = (typeof DISPLAY_CURRENCIES)[number];

// Currencies the user might enter cost basis in. Backend converts to EUR
// at write time, so this only affects what the user types into the form.
const INPUT_CURRENCIES = ['EUR', 'USD', 'GBP'] as const;

export default function HoldingsPage() {
  return (
    <NeedsAuth>
      <Inner />
    </NeedsAuth>
  );
}

function Inner() {
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [refreshNote, setRefreshNote] = useState<string | null>(null);
  const [displayCcy, setDisplayCcy] = useState<DisplayCurrency>('EUR');
  const { rates: fxRates } = useFxRates();

  // Refresh live prices ONCE per page reload (mount only). Subsequent
  // mutations bump `tick` to re-list, but never re-hit the upstream price
  // APIs — a row with a missing/zero price exposes a per-row refresh
  // button instead so the user opts in. This keeps us under Yahoo's
  // unauthenticated rate limit even if the user is heavy-handed with
  // edits.
  useEffect(() => {
    let cancelled = false;
    const client = getClient();
    if (!client) return;
    void (async () => {
      const refresh = await client.POST('/api/holdings/refresh-prices', {});
      if (cancelled) return;
      if (!refresh.error && refresh.data) {
        const { updated, failed_tickers } = refresh.data;
        const parts: string[] = [];
        if (updated > 0) parts.push(`updated ${updated}`);
        if (failed_tickers && failed_tickers.length > 0) {
          parts.push(`failed: ${failed_tickers.join(', ')}`);
        }
        setRefreshNote(parts.length ? parts.join(' · ') : null);
      }
      const { data, error } = await client.GET('/api/holdings');
      if (cancelled) return;
      if (error) setError(JSON.stringify(error));
      else {
        setHoldings(data?.holdings ?? []);
        setError(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // After mutations (create/edit/delete) we just re-list — no upstream
  // refresh. `tick === 0` is the initial mount which the effect above
  // already handles, so skip it here.
  useEffect(() => {
    if (tick === 0) return;
    let cancelled = false;
    const client = getClient();
    if (!client) return;
    void (async () => {
      const { data, error } = await client.GET('/api/holdings');
      if (cancelled) return;
      if (error) setError(JSON.stringify(error));
      else {
        setHoldings(data?.holdings ?? []);
        setError(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  // Patch a single holding into local state after a per-row refresh —
  // avoids re-fetching the whole list.
  const replaceHolding = (next: Holding) =>
    setHoldings((prev) => prev.map((h) => (h.id === next.id ? next : h)));

  // Aggregate market value / cost basis / P/L. All amounts come from the
  // server already in EUR — the toggle below converts at display time
  // only.
  const totals = useMemo(() => {
    let value = 0;
    let cost = 0;
    let pl = 0;
    for (const h of holdings) {
      if (h.value !== null && h.value !== undefined) value += Number(h.value);
      if (h.cost_basis !== null && h.cost_basis !== undefined)
        cost += Number(h.cost_basis);
      if (h.unrealized_pl !== null && h.unrealized_pl !== undefined)
        pl += Number(h.unrealized_pl);
    }
    return { value, cost, pl };
  }, [holdings]);

  const showValue = fromEur(totals.value, displayCcy, fxRates) ?? 0;
  const showCost = fromEur(totals.cost, displayCcy, fxRates) ?? 0;
  const showPl = fromEur(totals.pl, displayCcy, fxRates) ?? 0;

  return (
    <div className="flex max-w-5xl flex-col gap-6">
      <PageTitle
        actions={
          <div className="flex items-center gap-3">
            {refreshNote && <span className="text-xs text-fg-muted">{refreshNote}</span>}
            <CurrencyToggle value={displayCcy} onChange={setDisplayCcy} />
          </div>
        }
      >
        Holdings
      </PageTitle>

      {holdings.length > 0 && (
        <Card>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="flex flex-col">
              <span className="text-xs uppercase tracking-wide text-(--color-muted)">
                Market value
              </span>
              <span className="font-mono tabular-nums text-lg">
                {money(showValue, displayCcy)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs uppercase tracking-wide text-(--color-muted)">
                Cost basis
              </span>
              <span className="font-mono tabular-nums text-lg">
                {money(showCost, displayCcy)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs uppercase tracking-wide text-(--color-muted)">
                Unrealized P/L
              </span>
              <span
                className={
                  'font-mono tabular-nums text-lg ' +
                  (showPl >= 0
                    ? 'text-(--color-positive)'
                    : 'text-(--color-negative)')
                }
              >
                {money(showPl, displayCcy)}
                {totals.cost !== 0 && (
                  <span className="ml-2 text-xs opacity-80">
                    {pct(totals.pl / totals.cost)}
                  </span>
                )}
              </span>
            </div>
          </div>
        </Card>
      )}

      <Card title="Add a holding">
        <CreateForm onCreated={() => setTick((t) => t + 1)} />
      </Card>

      {error && (
        <Card>
          <p className="text-sm text-(--color-negative)">Error: {error}</p>
        </Card>
      )}

      <Card>
        {holdings.length === 0 ? (
          <p className="text-sm text-(--color-muted)">
            No holdings yet. Add a stock or crypto position above.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-(--color-card-border) text-left text-(--color-muted)">
                <tr>
                  <th className="py-2 pr-4 font-medium">Kind</th>
                  <th className="py-2 pr-4 font-medium">Ticker</th>
                  <th className="py-2 pr-4 font-medium">Name</th>
                  <th className="py-2 pr-4 font-medium">Held at</th>
                  <th className="py-2 pr-4 font-medium text-right">Qty</th>
                  <th className="py-2 pr-4 font-medium text-right">Avg cost</th>
                  <th className="py-2 pr-4 font-medium text-right">Live price</th>
                  <th className="py-2 pr-4 font-medium text-right">Value</th>
                  <th className="py-2 pr-4 font-medium text-right">P/L</th>
                  <th className="py-2 pr-4 font-medium">Updated</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {holdings.map((h) =>
                  editingId === h.id ? (
                    <EditRow
                      key={h.id}
                      holding={h}
                      displayCcy={displayCcy}
                      fxRates={fxRates}
                      onCancel={() => setEditingId(null)}
                      onSaved={() => {
                        setEditingId(null);
                        setTick((t) => t + 1);
                      }}
                      onDeleted={() => {
                        setEditingId(null);
                        setTick((t) => t + 1);
                      }}
                    />
                  ) : (
                    <Row
                      key={h.id}
                      holding={h}
                      displayCcy={displayCcy}
                      fxRates={fxRates}
                      onEdit={() => setEditingId(h.id)}
                      onRefreshed={replaceHolding}
                    />
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function CurrencyToggle({
  value,
  onChange,
}: {
  value: DisplayCurrency;
  onChange: (v: DisplayCurrency) => void;
}) {
  return (
    <PillGroup>
      {DISPLAY_CURRENCIES.map((c) => (
        <Pill key={c} active={c === value} onClick={() => onChange(c)}>
          {c === 'EUR' ? '€ EUR' : '$ USD'}
        </Pill>
      ))}
    </PillGroup>
  );
}

function Row({
  holding: h,
  displayCcy,
  fxRates,
  onEdit,
  onRefreshed,
}: {
  holding: Holding;
  displayCcy: DisplayCurrency;
  fxRates: FxRates | null;
  onEdit: () => void;
  onRefreshed: (next: Holding) => void;
}) {
  // All amounts on the wire are EUR — convert here for display only.
  const avgDisp = fromEur(h.avg_price, displayCcy, fxRates);
  const liveDisp = fromEur(h.unit_price, displayCcy, fxRates);
  const valDisp = fromEur(h.value, displayCcy, fxRates);
  const plDisp = fromEur(h.unrealized_pl, displayCcy, fxRates);
  const plPctNum =
    h.unrealized_pl_pct !== null && h.unrealized_pl_pct !== undefined
      ? Number(h.unrealized_pl_pct)
      : null;
  const plClass =
    plDisp === null
      ? ''
      : plDisp >= 0
        ? 'text-(--color-positive)'
        : 'text-(--color-negative)';

  // Show the manual refresh button when the price is null or numerically
  // zero — those are the cases where the on-mount auto-refresh failed
  // (404, 429, etc.) and the user needs to opt in.
  const priceMissing =
    h.unit_price === null ||
    h.unit_price === undefined ||
    Number(h.unit_price) === 0;
  const [refreshing, setRefreshing] = useState(false);
  const [refreshErr, setRefreshErr] = useState<string | null>(null);

  async function refreshOne() {
    const client = getClient();
    if (!client) return;
    setRefreshing(true);
    setRefreshErr(null);
    const { data, error } = await client.POST(
      '/api/holdings/{holding_id}/refresh-price',
      { params: { path: { holding_id: h.id } } },
    );
    setRefreshing(false);
    if (error) {
      // FastAPI HTTPException puts the message in `detail`; surface it
      // verbatim so the cool-down "try again in N min" hint reaches the
      // user.
      const msg =
        (error as { detail?: string } | undefined)?.detail ?? 'failed';
      setRefreshErr(msg);
      return;
    }
    if (data) onRefreshed(data);
  }

  return (
    <tr className="border-b border-(--color-card-border)/50 hover:bg-(--color-card-border)/30">
      <td className="py-2 pr-4 capitalize text-(--color-muted)">{h.kind}</td>
      <td className="py-2 pr-4 font-mono">{h.ticker}</td>
      <td className="py-2 pr-4 text-(--color-muted)">{h.name ?? '—'}</td>
      <td className="py-2 pr-4 text-(--color-muted)">{h.held_at ?? '—'}</td>
      <td className="py-2 pr-4 tabular-nums text-right font-mono">
        {Number(h.quantity).toLocaleString('en-IE', {
          maximumFractionDigits: 8,
        })}
      </td>
      <td className="py-2 pr-4 tabular-nums text-right font-mono">
        {avgDisp !== null ? money(avgDisp, displayCcy, 4) : '—'}
      </td>
      <td className="py-2 pr-4 tabular-nums text-right font-mono">
        <div className="inline-flex items-center gap-1 justify-end">
          <span>
            {liveDisp !== null && Number(h.unit_price) !== 0
              ? money(liveDisp, displayCcy, 4)
              : '—'}
          </span>
          {priceMissing && (
            <button
              onClick={refreshOne}
              disabled={refreshing}
              title={refreshErr ?? 'Fetch live price'}
              aria-label={`Refresh price for ${h.ticker}`}
              className={
                'rounded border border-(--color-card-border) px-1.5 py-0.5 text-xs leading-none ' +
                (refreshErr
                  ? 'text-(--color-negative)'
                  : 'text-(--color-muted) hover:bg-(--color-card-border)/40')
              }
            >
              {refreshing ? '…' : '↻'}
            </button>
          )}
        </div>
      </td>
      <td className="py-2 pr-4 tabular-nums text-right font-mono font-medium">
        {valDisp !== null ? money(valDisp, displayCcy) : '—'}
      </td>
      <td
        className={`py-2 pr-4 tabular-nums text-right font-mono ${plClass}`}
      >
        {plDisp !== null ? (
          <>
            {money(plDisp, displayCcy)}
            {plPctNum !== null && (
              <span className="block text-xs opacity-80">{pct(plPctNum)}</span>
            )}
          </>
        ) : (
          '—'
        )}
      </td>
      <td className="py-2 pr-4 text-xs text-(--color-muted)">
        {h.unit_price_at ? shortDate(h.unit_price_at) : '—'}
      </td>
      <td className="py-2 text-right">
        <Button variant="ghost" size="sm" onClick={onEdit}>
          edit
        </Button>
      </td>
    </tr>
  );
}

function EditRow({
  holding: h,
  displayCcy,
  fxRates,
  onCancel,
  onSaved,
  onDeleted,
}: {
  holding: Holding;
  displayCcy: DisplayCurrency;
  fxRates: FxRates | null;
  onCancel: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const [kind, setKind] = useState<Kind>(h.kind as Kind);
  const [ticker, setTicker] = useState(h.ticker);
  const [name, setName] = useState(h.name ?? '');
  const [heldAt, setHeldAt] = useState(h.held_at ?? '');
  const [quantity, setQuantity] = useState(String(h.quantity));
  // Edits are *always* in EUR — the user is editing the stored cost basis
  // directly. To enter a USD figure they should re-create or use the
  // (future) bulk import.
  const [avgPrice, setAvgPrice] = useState(
    h.avg_price !== null && h.avg_price !== undefined ? String(h.avg_price) : '',
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const liveDisp = fromEur(h.unit_price, displayCcy, fxRates);

  async function save() {
    const client = getClient();
    if (!client) return;
    setBusy(true);
    setErr(null);
    const body: Record<string, unknown> = {
      kind,
      ticker,
      name: name || null,
      held_at: heldAt,
      quantity,
      // EUR is the storage currency; the inline edit doesn't expose a
      // currency selector to keep the table tidy.
      unit_price_currency: 'EUR',
    };
    if (avgPrice.trim() === '') body.clear_avg_price = true;
    else body.avg_price = avgPrice;
    const { error } = await client.PATCH('/api/holdings/{holding_id}', {
      params: { path: { holding_id: h.id } },
      body: body as components['schemas']['HoldingUpdate'],
    });
    setBusy(false);
    if (error) setErr(JSON.stringify(error));
    else onSaved();
  }

  async function remove() {
    if (!confirm(`Delete ${ticker}?`)) return;
    const client = getClient();
    if (!client) return;
    setBusy(true);
    const { error } = await client.DELETE('/api/holdings/{holding_id}', {
      params: { path: { holding_id: h.id } },
    });
    setBusy(false);
    if (error) setErr(JSON.stringify(error));
    else onDeleted();
  }

  return (
    <tr className="border-b border-(--color-card-border)/50 bg-(--color-card-border)/20">
      <td className="py-2 pr-4">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as Kind)}
          className="rounded border border-(--color-card-border) bg-transparent px-1 py-0.5 text-xs"
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </td>
      <td className="py-2 pr-4">
        <input
          value={ticker}
          onChange={(e) => setTicker(e.target.value.toUpperCase())}
          className="rounded border border-(--color-card-border) bg-transparent px-2 py-0.5 text-xs font-mono w-24"
        />
      </td>
      <td className="py-2 pr-4">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="optional"
          className="rounded border border-(--color-card-border) bg-transparent px-2 py-0.5 text-xs w-40"
        />
      </td>
      <td className="py-2 pr-4">
        <input
          value={heldAt}
          onChange={(e) => setHeldAt(e.target.value)}
          placeholder="Revolut, Degiro…"
          className="rounded border border-(--color-card-border) bg-transparent px-2 py-0.5 text-xs w-32"
        />
      </td>
      <td className="py-2 pr-4 text-right">
        <input
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          className="rounded border border-(--color-card-border) bg-transparent px-2 py-0.5 text-xs font-mono text-right w-24"
        />
      </td>
      <td className="py-2 pr-4 text-right">
        <input
          value={avgPrice}
          onChange={(e) => setAvgPrice(e.target.value)}
          placeholder="EUR cost"
          title="Cost basis is stored in EUR. Type the EUR amount."
          className="rounded border border-(--color-card-border) bg-transparent px-2 py-0.5 text-xs font-mono text-right w-24"
        />
      </td>
      {/* Live price column is read-only — auto-fetched from upstream. */}
      <td className="py-2 pr-4 text-right text-xs text-(--color-muted) font-mono">
        {liveDisp !== null ? money(liveDisp, displayCcy, 4) : '—'}
      </td>
      <td className="py-2 pr-4 text-xs text-(--color-muted)" colSpan={3}>
        {err && <span className="text-(--color-negative)">{err}</span>}
      </td>
      <td className="py-2 text-right">
        <div className="flex justify-end gap-1">
          <Button variant="primary" size="sm" onClick={save} loading={busy}>
            save
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={remove} disabled={busy}>
            delete
          </Button>
        </div>
      </td>
    </tr>
  );
}

function CreateForm({ onCreated }: { onCreated: () => void }) {
  const [kind, setKind] = useState<Kind>('stock');
  const [ticker, setTicker] = useState('');
  const [name, setName] = useState('');
  const [heldAt, setHeldAt] = useState('');
  const [quantity, setQuantity] = useState('');
  const [avgPrice, setAvgPrice] = useState('');
  // The currency the *user is typing* — backend converts this to EUR
  // for storage. Default EUR since most users are EUR-native here.
  const [inputCcy, setInputCcy] = useState<string>('EUR');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!ticker.trim() || !quantity.trim()) return;
    const client = getClient();
    if (!client) return;
    setBusy(true);
    setErr(null);
    const body: HoldingCreate = {
      kind,
      ticker: ticker.trim(),
      name: name.trim() || null,
      quantity,
      // Source currency for the avg_price the user typed. Backend FX-converts.
      unit_price_currency: inputCcy,
      ...(avgPrice.trim() ? { avg_price: avgPrice } : {}),
      ...(heldAt.trim() ? { held_at: heldAt.trim() } : {}),
    };
    const { error } = await client.POST('/api/holdings', { body });
    setBusy(false);
    if (error) {
      setErr(JSON.stringify(error));
      return;
    }
    setTicker('');
    setName('');
    setHeldAt('');
    setQuantity('');
    setAvgPrice('');
    onCreated();
  }

  return (
    <form
      onSubmit={submit}
      className="grid grid-cols-1 sm:grid-cols-6 gap-3 text-sm items-end"
    >
      <label className="flex flex-col gap-1">
        <span className="text-xs text-(--color-muted)">Kind</span>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as Kind)}
          className="rounded-md border border-(--color-card-border) bg-transparent px-2 py-1.5"
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-(--color-muted)">Ticker</span>
        <input
          value={ticker}
          onChange={(e) => setTicker(e.target.value.toUpperCase())}
          placeholder={kind === 'crypto' ? 'BTC' : 'AAPL'}
          required
          className="rounded-md border border-(--color-card-border) bg-transparent px-2 py-1.5 font-mono"
        />
      </label>
      <label className="flex flex-col gap-1 sm:col-span-2">
        <span className="text-xs text-(--color-muted)">Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="optional"
          className="rounded-md border border-(--color-card-border) bg-transparent px-2 py-1.5"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-(--color-muted)">Held at</span>
        <input
          value={heldAt}
          onChange={(e) => setHeldAt(e.target.value)}
          placeholder="Revolut, Degiro, wallet…"
          className="rounded-md border border-(--color-card-border) bg-transparent px-2 py-1.5"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-(--color-muted)">Quantity</span>
        <input
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder="10"
          required
          inputMode="decimal"
          className="rounded-md border border-(--color-card-border) bg-transparent px-2 py-1.5 font-mono text-right"
        />
      </label>
      <div className="grid grid-cols-3 gap-1">
        <label className="flex flex-col gap-1 col-span-2">
          <span
            className="text-xs text-(--color-muted)"
            title="What you paid per unit (cost basis). Stored in EUR; converted from the input currency below."
          >
            Avg price
          </span>
          <input
            value={avgPrice}
            onChange={(e) => setAvgPrice(e.target.value)}
            placeholder="cost basis"
            inputMode="decimal"
            className="rounded-md border border-(--color-card-border) bg-transparent px-2 py-1.5 font-mono text-right"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-(--color-muted)">Input ccy</span>
          <select
            value={inputCcy}
            onChange={(e) => setInputCcy(e.target.value)}
            className="rounded-md border border-(--color-card-border) bg-transparent px-2 py-1.5 font-mono"
          >
            {INPUT_CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex items-center gap-3 sm:col-span-6">
        <Button variant="primary" type="submit" loading={busy}>
          Add holding
        </Button>
        <span className="text-xs text-fg-muted">
          Stored in EUR. Live price auto-refreshed from Yahoo / CoinGecko on load.
        </span>
        {err && <span className="text-xs text-negative">{err}</span>}
      </div>
    </form>
  );
}
